import {
  WSAlert,
  WSFlexBox,
  WSList,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useLocation } from "react-router-dom";
import { useEmployeeTaxFormsChecklistViewByFormIds } from "../hooks/useEmployeeTaxFormsChecklistViewByFormIds";
import { ChecklistRows } from "./ChecklistRows";

type Props = {
  formIds: string[];
};

/**
 * Inline tax-forms checklist rendered as the requirement-specific content on a
 * contractor's requirement details page (wired through the EmployeeTaxForms
 * requirement config's `requirementAdditionalContent`). This replaces the old
 * standalone checklist screen: the forms and their statuses live on the
 * requirement page itself, each row opens its own form guide, and the renderer
 * returns here (via the captured `backPath`) after submit.
 *
 * `formIds` comes straight off the requirement's own dataSourceMetadata (see
 * getEmployeeTaxFormsFormIds) — no separate lookup needed to discover which
 * instances apply. Navigation into each form only needs the form's own
 * instance id — the engine resolves everything else from the row itself.
 */
export const EmployeeTaxFormsRequirementChecklist: React.FC<Props> = ({
  formIds
}) => {
  const location = useLocation();
  const backPath = `${location.pathname}${location.search}`;
  const { isLoading, orderedItems, allCompleted } =
    useEmployeeTaxFormsChecklistViewByFormIds(formIds);
  const isChecklistLoading = formIds.length > 0 && isLoading;

  if (isChecklistLoading) {
    return (
      <WSFlexBox.Center py="2XL">
        <WSLoader.Spinner size="M" />
      </WSFlexBox.Center>
    );
  }

  if (orderedItems.length === 0) {
    return null;
  }

  return (
    <WSList gap="M">
      <WSText.Heading5>Forms</WSText.Heading5>
      {allCompleted ? (
        <WSAlert theme="success">
          All tax forms are submitted. You can review any form below.
        </WSAlert>
      ) : (
        <WSText.ParagraphSm color="gray500">
          Complete and sign each form below. We&apos;ll save your progress as
          you go.
        </WSText.ParagraphSm>
      )}
      <ChecklistRows items={orderedItems} backPath={backPath} />
    </WSList>
  );
};
